import { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Accordion, AccordionDetails, Box, Typography } from "@mui/material";
import DeleteIcon from "../../../../assets/svg-fc/DeleteIcon";
import DragSectionIcon from "../../../../assets/svg-fc/DragSectionIcon";
import EditIcon from "../../../../assets/svg-fc/EditIcon";
import RichTextEditor from "../../../../components/reusable-components/rich-text-editor/RichTextEditor";
import { IAttachmentFile } from "../../../../interfaces/corporateEvent";
import { BUTTON_COLOR, BUTTON_VARIANT } from "../../../../types/coreButtonTypes";
import { ICreateQuoteFormSection } from "../../../../types/quoteFormTypes";
import CoreButtons from "../../../core-components/button/CoreButtons";
import DetailListInput from "../../../../components/reusable-components/detail-list-input/DetailListInput";
import AddAttachmentPromptBox from "../../../../components/reusable-components/add-attachment-prompt-box/AddAttachmentPromptBox";
import { MAX_SELECTABLE_PHOTOS } from "../../../../constants/commonConstants";
import "./menuAndServicesSection.scss";

interface IMenuAndServicesSectionProps {
	className?: string;
	section: ICreateQuoteFormSection;
	richTextPlaceholder: string;
	showHeader?: boolean;
	showHeaderActionButtons?: boolean;
	isCollapsed?: boolean;
	setOpenPhotoLibrary: (open: boolean) => void;
	onTitleChange?: (title: string) => void;
	onContentChange: (content: string) => void;
	onDelete?: () => void;
	removePhoto: (photoId: string) => void;
}

const MenuAndServicesSection = ({
	className = "",
	section,
	richTextPlaceholder,
	showHeader = true,
	showHeaderActionButtons = true,
	isCollapsed = false,
	setOpenPhotoLibrary,
	onTitleChange,
	onContentChange,
	onDelete,
	removePhoto
}: IMenuAndServicesSectionProps) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: section?.clientId ?? `order-${section?.displayOrder}`,
		disabled: !showHeader,
	});
	const [isEditing, setIsEditing] = useState(false);

	const style = {
		transform: CSS.Translate.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	return (
		<Accordion
			ref={setNodeRef}
			style={style}
			className={`sectionContainer ${className}`}
			expanded={!isCollapsed}
		>
			{showHeader && (
				<Box className="categoryHeader">
					{isEditing ? (
						<DetailListInput
							value={section?.sectionTitle}
							setIsEditing={setIsEditing}
							onUpdate={(value) => onTitleChange?.(value)}
						/>
					) : (
						<>
							<Box className="categoryDisplay">
								<Typography variant="body2" className="categoryLabel">
									{section?.sectionTitle}
								</Typography>
								{!isCollapsed && (
									<Box className="editIcon" onClick={() => setIsEditing(true)}>
										<EditIcon />
									</Box>
								)}
							</Box>
							{showHeaderActionButtons && (
								<Box className="headerActionButtons">
									{!isCollapsed && (
										<CoreButtons
											className="deleteButton"
											buttonText="Delete"
											variant={BUTTON_VARIANT.OUTLINED}
											color={BUTTON_COLOR.ERROR}
											startIcon={<DeleteIcon />}
											onclick={onDelete}
										/>
									)}
									{attributes && listeners && (
										<Box {...attributes} {...listeners} className="dragHandle" style={{ cursor: "grab" }}>
											<DragSectionIcon />
										</Box>
									)}
								</Box>
							)}
						</>
					)}
				</Box>
			)}
			<AccordionDetails className="sectionContent">
				<RichTextEditor
					value={section?.content || ""}
					onChange={onContentChange}
					placeholder={richTextPlaceholder}
				/>
				<AddAttachmentPromptBox
				  removePhoto={removePhoto}
					attachments={section?.attachments}
					maxAttachments={MAX_SELECTABLE_PHOTOS}
					setIsOpen={setOpenPhotoLibrary}
				/>
				{/* <AddAndViewAttachments
					id={`${section?.displayOrder}`}
					className="menuAttachmentsWrapper"
					title={
						<>
							Attachments <span>(optional)</span>
						</>
					}
					attachments={section?.attachments || []}
					disabled={section?.attachments?.length === MAX_ATTACHMENTS}
					showUploadIcon={false}
					attachmentHelperText="Upload Image"
					attachmentTypeText="PNG, JPG (max 10 MB)"
					fileSizeLimit={10}
					showImportButton={false}
					enableCompression={true}
					handleSetAttachments={onAttachmentsChange}
				/> */}
			</AccordionDetails>
		</Accordion>
	);
};

export default MenuAndServicesSection;
